import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { X, AlertCircle, ImageOff } from 'lucide-react';
import { SkuVtex, FotoLink } from '../similares/ComparativoSimilares';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatMoney = (v) =>
  (v === null || v === undefined || v === '')
    ? '—'
    : `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const somaVals = (obj) => Object.values(obj || {}).reduce((a, b) => a + Number(b || 0), 0);

// Modal rico de "Propor Similar" na tela de Cancelamentos AES.
// Comparação ORIGINAL-primeiro (estilo Buscar Produto) + custo + preço de venda,
// buscando ao vivo em /produtos/sugerir-similar. Confirmar chama o mesmo
// POST /cancelamentos/{id}/propor-similares (com detalhe p/ preservar nome/id na msg).
export default function SimilarProporModal({ item, onClose, onSaved }) {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [sel, setSel] = useState(() => new Set());
  const [skuManual, setSkuManual] = useState('');
  const [saving, setSaving] = useState(false);
  const [imgMap, setImgMap] = useState({});
  const [imgFalhou, setImgFalhou] = useState({});
  const [linkMap, setLinkMap] = useState({});   // cod_terceiro -> cadastro na VTEX
  const [lojaMap, setLojaMap] = useState({});   // cod_terceiro -> página na loja VTEX (clique na foto)

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const entrega = String(item.numero_pedido || '').split('.')[0];
        const params = { sku: item.codigo_item_vtex, tipo: 'similar' };
        if (entrega) params.entrega = entrega;
        const res = await axios.get(`${API_URL}/api/produtos/sugerir-similar`, { params, headers: getAuthHeader() });
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || e?.message || 'Erro ao buscar similares');
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [item, getAuthHeader]);

  const orig = data?.original;
  const propostos = data?.propostos || [];

  // Imagens (lazy) — original + propostos
  useEffect(() => {
    if (!orig) return;
    const skus = [orig, ...propostos].map(p => p?.cod_terceiro).filter(Boolean);
    skus.filter(sk => !imgMap[sk] && !imgFalhou[sk]).forEach(sk => {
      axios.get(`${API_URL}/api/produtos/imagem/${encodeURIComponent(sk)}`, { headers: getAuthHeader() })
        .then(r => {
          if (r.data?.image_url) setImgMap(p => ({ ...p, [sk]: r.data.image_url }));
          else setImgFalhou(p => ({ ...p, [sk]: true }));
          if (r.data?.vtex_admin_url) setLinkMap(p => ({ ...p, [sk]: r.data.vtex_admin_url }));
          if (r.data?.vtex_loja_url) setLojaMap(p => ({ ...p, [sk]: r.data.vtex_loja_url }));
        })
        .catch(() => setImgFalhou(p => ({ ...p, [sk]: true })));
    });
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (sku) => setSel(prev => {
    const n = new Set(prev);
    n.has(sku) ? n.delete(sku) : n.add(sku);
    return n;
  });
  const totalSel = sel.size + (skuManual.trim() ? 1 : 0);

  const propor = async () => {
    const skus = [...Array.from(sel)];
    if (skuManual.trim()) skus.push(skuManual.trim().toUpperCase());
    if (skus.length === 0) { toast.error('Marque um similar ou digite o SKU'); return; }
    // detalhe preserva nome/id do similar p/ a mensagem gerada depois
    const detalhe = propostos.filter(p => sel.has(p.cod_terceiro))
      .map(p => ({ sku: p.cod_terceiro, nome: p.descricao, id_bseller: p.id_item_bseller }));
    setSaving(true);
    try {
      const resp = await axios.post(`${API_URL}/api/cancelamentos/${item.id}/propor-similares`,
        { skus, detalhe }, { headers: getAuthHeader() });
      toast.success('Similar(es) proposto(s)');
      // devolve o registro gravado: a linha se corrige sem esperar "Atualizar"
      onSaved?.(resp?.data?.cancelamento);
    } catch (e) {
      toast.error('Erro: ' + (e?.response?.data?.detail || e?.message));
    } finally { setSaving(false); }
  };

  const seguirCancelamento = async () => {
    setSaving(true);
    try {
      const resp = await axios.post(`${API_URL}/api/cancelamentos/${item.id}/seguir-cancelamento`,
        {}, { headers: getAuthHeader() });
      toast.success('Seguindo com cancelamento');
      onSaved?.(resp?.data?.cancelamento);
    } catch (e) {
      toast.error('Erro: ' + (e?.response?.data?.detail || e?.message));
    } finally { setSaving(false); }
  };

  // Δ de custo do proposto vs. original (vermelho = mais caro, verde = mais barato)
  const custoDelta = (p) => {
    const co = Number(orig?.custo), cp = Number(p?.custo);
    if (!isFinite(co) || !isFinite(cp) || co === 0) return null;
    const dif = cp - co;
    if (!dif) return null;
    return <span className={`text-[11px] ml-1 ${dif > 0 ? 'text-red-600' : 'text-emerald-600'}`}>({dif > 0 ? '+' : ''}{formatMoney(dif)})</span>;
  };

  const imgCell = (p, idx) => (
    <td key={idx} className={`px-3 py-2 ${idx === 0 ? 'bg-amber-50/30 dark:bg-amber-950/10' : ''}`}>
      {imgMap[p.cod_terceiro]
        ? <FotoLink img={imgMap[p.cod_terceiro]} loja={lojaMap[p.cod_terceiro]} alt={p.cod_terceiro} className="h-24 w-24 object-contain rounded border bg-white" />
        : imgFalhou[p.cod_terceiro]
          ? <span className="inline-flex h-24 w-24 items-center justify-center rounded border bg-slate-50 text-slate-300"><ImageOff className="h-6 w-6" /></span>
          : <span className="inline-flex h-24 w-24 items-center justify-center rounded border bg-slate-50 text-[10px] text-slate-400">carregando…</span>}
    </td>
  );

  const linhas = [
    { label: 'cod_terceiro', get: p => <SkuVtex sku={p.cod_terceiro} url={linkMap[p.cod_terceiro]} /> },
    { label: 'ID Bseller', get: p => p.id_item_bseller || '—' },
    { label: 'Cód. fornecedor', get: p => p.codigo_fornec || '—' },
    { label: 'EAN', get: p => p.ean || '—' },
    { label: 'Descrição', get: p => p.descricao || '—' },
    { label: 'Status', get: p => `${p.status || '—'}${p.abc_class ? ` · classe ${p.abc_class}` : ''}` },
    // Subcategoria na tela: com o escopo amplo o proposto pode vir de outra
    // (Eletroportateis x Cozinha e Utilidades = eletrico x manual). Sem ver isso,
    // "Espremedor Manual" passa por substituto de espremedor eletrico.
    { label: 'Subcategoria', get: p => p.subcategoria || p.categoria || '—' },
    { label: 'Peças', get: p => (p.pecas != null ? `${p.pecas} peças` : '—') },
    { label: 'Preço de venda', get: p => <span className="font-semibold text-emerald-700">{formatMoney(p.preco_venda)}</span> },
    { label: 'Custo (últ. compra)', get: p => <span>{formatMoney(p.custo)}{p !== orig && custoDelta(p)}</span> },
    { label: 'Físico (total)', get: p => somaVals(p.filiais_fisico) || 0 },
    { label: 'XD (total)', get: p => somaVals(p.xd_por_estab) || 0 },
    { label: 'UFs com estoque', get: p => (p.ufs_com_estoque || []).join(', ') || '—' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-5xl my-8" onClick={e => e.stopPropagation()}>
        {/* Cabeçalho */}
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b">
          <div className="text-sm">
            <div className="font-semibold flex items-center gap-2">🔄 Propor Similar</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {item.produto || item.codigo_item_vtex} · Entrega {item.numero_pedido}
              {(item.nome_cliente || item.cliente_confirmado_nome) ? ` · ${item.nome_cliente || item.cliente_confirmado_nome}` : ''}
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
        </div>

        {/* Aviso do motor: preço relaxado, tensão sem confirmação ou escopo
            aberto fora da subcategoria. Antes o backend mandava e a tela
            engolia — a atendente ofertava sem saber que houve fallback. */}
        {/* Regra do canal (NiceQuest nao sai de SP): nota fixa, aparece mesmo quando
            os similares estao todos certos. */}
        {data?.nota_canal && !loading && (
          <div className="mx-4 mt-3 rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800 px-3 py-2 text-xs font-semibold text-orange-900 dark:text-orange-200 flex items-start gap-1.5">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{data.nota_canal}</span>
          </div>
        )}

        {data?.aviso_fallback && !loading && (
          <div className="mx-4 mt-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-3 py-2 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-1.5">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{data.aviso_fallback}</span>
          </div>
        )}

        {/* Comparação */}
        <div className="p-4 overflow-x-auto">
          {loading ? <div className="py-10 text-center text-muted-foreground">Buscando similares…</div>
            : error ? <div className="py-10 text-center text-red-600">{error}</div>
            : !orig ? <div className="py-10 text-center text-muted-foreground">Produto original não encontrado no catálogo.</div>
            : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="px-3 py-2 text-left bg-slate-50 dark:bg-slate-800 w-44">&nbsp;</th>
                    <th className="px-3 py-2 text-left align-top bg-amber-50/40 dark:bg-amber-950/20">
                      <div className="font-semibold text-sm"><SkuVtex sku={orig.cod_terceiro} url={linkMap[orig.cod_terceiro]} /> — original</div>
                    </th>
                    {propostos.map((p, i) => (
                      <th key={p.cod_terceiro} className="px-3 py-2 text-left align-top">
                        <label className="flex items-start gap-1.5 cursor-pointer">
                          <input type="checkbox" checked={sel.has(p.cod_terceiro)} onChange={() => toggle(p.cod_terceiro)} className="mt-0.5 accent-green-600" />
                          <div>
                            <div className="font-semibold text-sm"><SkuVtex sku={p.cod_terceiro} url={linkMap[p.cod_terceiro]} /> — Proposto ({i + 1})</div>
                            {/* Etiquetas de valor: desde 02/09 o preço não corta
                                mais, então quem decide precisa ver o impacto. */}
                            {(p.venda_pct != null || p.acima_do_pago || p.custo_situacao === 'acima_do_teto' || p.pecas != null) && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {p.pecas != null && (
                                  <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                    {p.pecas} peças
                                  </span>
                                )}
                                {p.venda_pct != null && (
                                  <span
                                    className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold ${
                                      p.venda_situacao === 'muito_abaixo' ? 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300'
                                      : p.venda_situacao === 'muito_acima' ? 'bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-300'
                                      : p.venda_situacao === 'ate_20_abaixo' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                                      : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'}`}
                                    title="Preço de venda do proposto em relação ao original"
                                  >
                                    {p.venda_pct}% do valor
                                  </span>
                                )}
                                {p.acima_do_pago && (
                                  <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300"
                                    title="Custa mais do que o cliente pagou — a troca dá prejuízo">
                                    custa + que o pago
                                  </span>
                                )}
                                {p.custo_situacao === 'acima_do_teto' && !p.acima_do_pago && (
                                  <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/20 dark:text-amber-300 dark:border-amber-800"
                                    title={p.custo_dif != null ? `Custo R$ ${p.custo_dif.toFixed(2).replace('.', ',')} acima do original (teto era R$ 30)` : 'Custo acima do teto de R$ 30'}>
                                    custo +R$ {p.custo_dif != null ? p.custo_dif.toFixed(2).replace('.', ',') : '?'}
                                  </span>
                                )}
                                {p.custo_situacao === 'sem_custo' && (
                                  <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                                    title="Custo não cadastrado — não dá pra avaliar margem">
                                    sem custo
                                  </span>
                                )}
                              </div>
                            )}
                            {p.escopo_amplo && (
                              <div className="mt-0.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100"
                                title="Veio de outra subcategoria: confira se o tipo bate (elétrico x manual) antes de ofertar">
                                fora da subcategoria
                              </div>
                            )}
                            {p.alerta_filial && (
                              <div className="mt-1 text-xs flex items-start gap-1 text-red-600 font-medium">
                                <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /><span>{p.alerta_filial}</span>
                              </div>
                            )}
                          </div>
                        </label>
                      </th>
                    ))}
                    {propostos.length === 0 && (
                      <th className="px-3 py-4 text-center text-slate-500 font-normal">Nenhum similar com estoque — digite um SKU abaixo.</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <th className="px-3 py-1.5 text-left font-medium text-slate-600 bg-slate-50 dark:bg-slate-800">Imagem</th>
                    {[orig, ...propostos].map((p, idx) => imgCell(p, idx))}
                  </tr>
                  {linhas.map(({ label, get }) => (
                    <tr key={label} className="border-b">
                      <th className="px-3 py-1.5 text-left font-medium text-slate-600 bg-slate-50 dark:bg-slate-800">{label}</th>
                      <td className="px-3 py-1.5 bg-amber-50/30 dark:bg-amber-950/10">{get(orig) ?? '—'}</td>
                      {propostos.map(p => <td key={p.cod_terceiro + label} className="px-3 py-1.5">{get(p) ?? '—'}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>

        {/* Ações */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t bg-slate-50/60 dark:bg-slate-800/30">
          <input type="text" value={skuManual} onChange={e => setSkuManual(e.target.value.toUpperCase())}
            placeholder="ou digite o SKU do similar"
            className="text-sm rounded-md border px-2 py-1 w-56 bg-background" />
          <div className="flex items-center gap-2">
            <button onClick={seguirCancelamento} disabled={saving}
              className="text-sm px-3 py-1.5 rounded-md border text-orange-700 border-orange-300 hover:bg-orange-50 dark:hover:bg-orange-950/30 disabled:opacity-50">
              Seguir cancelamento
            </button>
            <button onClick={propor} disabled={saving || totalSel === 0}
              className="text-sm px-4 py-1.5 rounded-md font-semibold bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
              Propor ({totalSel})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
